import axios from 'axios';

import { env } from 'config';
import { CustomerCancelReason } from 'enums';
import { FormFieldOverride, Order, OrderReceipt, UpdateOrderDto } from 'types';

export type CancelFeedback = {
  reasons: CustomerCancelReason[];
  comment: string;
};

const url = `${env.apiUrl}/v1/customer/orders`;

const getOne = async (id: string): Promise<Order> =>
  (await axios.get(`${url}/${id}`)).data;

const update = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateOrderDto;
}): Promise<Order> => (await axios.patch(`${url}/${id}`, data)).data;

const cancel = async (id: string, feedback?: CancelFeedback): Promise<Order> =>
  (await axios.post(`${url}/${id}/cancel`, feedback ?? {})).data;

const startPayment = async (id: string): Promise<Order> =>
  (await axios.post(`${url}/${id}/start-payment`)).data;

const confirmPayment = async (id: string): Promise<Order> =>
  (await axios.post(`${url}/${id}/confirm-payment`)).data;

const getPaymentFields = async (id: string): Promise<FormFieldOverride[]> =>
  (await axios.get(`${url}/${id}/payment-fields`)).data;

const uploadReceipt = async ({
  orderId,
  body,
}: {
  orderId: string;
  body: any;
}) => (await axios.post(`${url}/${orderId}/receipts`, body)).data;

const getOrderReceipts = async (orderId: string): Promise<OrderReceipt[]> =>
  (await axios.get(`${url}/${orderId}/receipts`)).data;

const startPaymentSync = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateOrderDto;
}): Promise<Order> =>
  (await axios.post(`${url}/${id}/start-payment-sync`, data)).data;

const startRequisitesCascade = async (id: string): Promise<Order> =>
  (await axios.post(`${url}/${id}/start-requisites-cascade`)).data;

// ecom: submit the 3DS/OTP code the customer entered on the SCI gateway (ecom_platform_all).
// PCI: code is transient, never stored client-side beyond the request.
const submitConfirmCode = async ({
  id,
  confirmCode,
}: {
  id: string;
  confirmCode: string;
}): Promise<Order> =>
  (await axios.post(`${url}/${id}/confirm`, { confirm_code: confirmCode }))
    .data;

const offerAlternativeAmounts = async (id: string): Promise<number[]> =>
  (await axios.post(`${url}/${id}/alternative-amounts`)).data;

const changeAmount = async ({
  id,
  amount,
}: {
  id: string;
  amount: number;
}): Promise<Order> =>
  (await axios.post(`${url}/${id}/change-amount`, { amount })).data;

const resetTradeMethod = async (id: string): Promise<Order> =>
  (await axios.post(`${url}/${id}/reset-trade-method`)).data;

export const ordersApi = {
  getOne,
  update,
  cancel,
  startPayment,
  confirmPayment,
  getPaymentFields,
  uploadReceipt,
  getOrderReceipts,
  startPaymentSync,
  startRequisitesCascade,
  submitConfirmCode,
  offerAlternativeAmounts,
  changeAmount,
  resetTradeMethod,
};
